import React, { useEffect, useRef, useState } from "react";
import { Drawer, Button, DatePicker, Spin, Tag, Space, Divider, Input } from "antd";
import { CheckOutlined, EditOutlined as RequestChangesIcon, CalendarOutlined, CommentOutlined, CloseOutlined, HighlightOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import { linkifyHtml } from "./BlogDescriptionModal";
import { selectionInsideElement, buildCommentHighlights, findOccurrenceIndex } from "../../helper/writingSelection";

const { TextArea } = Input;

// Admin's counterpart to brand-manager-Imergey's BlogWriteVersionEditor.js —
// same read-only "open in editor" + approve/request-changes + schedule flow,
// backed by the same shared BlogWriteService methods. One difference: Admin
// has no "submit for internal review" endpoint (that's a BM-only gate onto
// BS/BH — see AccessModuleRouter's onlyBrandManager on that route), so once
// promoted this just confirms the Blog was created instead of offering that
// step; Admin manages it further from the Blogs list like any other blog.
function BlogWriteVersionEditor({ open, version, requestDetails, onClose, onApprove, onRequestChanges, onPromoted, deciding }) {
  const { request } = useRequest();

  const [loadingHtml, setLoadingHtml] = useState(false);
  const [html, setHtml] = useState("");
  const [conversionWarnings, setConversionWarnings] = useState(0);

  const [scheduleDate, setScheduleDate] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [promotedBlog, setPromotedBlog] = useState(null);

  // ── Highlight-and-comment (supplements, does not replace, the existing
  // free-text Request Changes note above) ──────────────────────────────────
  const contentRef = useRef(null);
  // Positioned ancestor the popovers are absolutely placed against — so they
  // scroll along with the content instead of staying pinned to the viewport.
  const wrapperRef = useRef(null);
  const [versionComments, setVersionComments] = useState([]);
  // { text, top, left } of the just-made selection, in coords relative to
  // wrapperRef (so it stays anchored to the selection as the drawer scrolls)
  // — null when nothing is selected right now.
  const [pendingSelection, setPendingSelection] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  // The comment(s) shown when an existing highlight is clicked: { comments, top, left }.
  const [activePopup, setActivePopup] = useState(null);

  useEffect(() => {
    if (open) {
      setPromotedBlog(null);
      setPendingSelection(null);
      setCommentDraft("");
      setActivePopup(null);
    }
  }, [open]);

  const fetchVersionComments = () => {
    if (!requestDetails?._id || !version?._id) return;
    request({
      url: apiPath.blogWriteRequestComments(requestDetails._id),
      method: "GET",
      onSuccess: (data) => {
        const all = data?.data?.comments || [];
        setVersionComments(all.filter((c) => c.anchorText && String(c.versionId) === String(version._id)));
      },
      onError: () => {},
    });
  };

  useEffect(() => {
    if (!open || !version) return;
    fetchVersionComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, version?._id]);

  const handleContentMouseUp = () => {
    if (!selectionInsideElement(contentRef.current)) return;
    const sel = window.getSelection();
    const text = sel ? sel.toString().trim() : "";
    if (!text || sel.rangeCount === 0) {
      setPendingSelection(null);
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    if (!rect || (!rect.top && !rect.left)) {
      setPendingSelection(null);
      return;
    }
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    // Computed now, while the range still reflects exactly what the user
    // selected — the same sentence can appear more than once in a document,
    // and matching by text alone at render time always resolves to the
    // first occurrence, so without this a highlight on a LATER duplicate
    // would incorrectly land on an earlier one instead.
    const anchorIndex = findOccurrenceIndex(contentRef.current, sel.getRangeAt(0), text);
    setPendingSelection({ text, anchorIndex, top: rect.bottom - wrapperRect.top + 6, left: rect.left - wrapperRect.left });
  };

  const submitInlineComment = () => {
    if (!commentDraft.trim() || !pendingSelection) return;
    setSavingComment(true);
    request({
      url: apiPath.blogWriteRequestComments(requestDetails._id),
      method: "POST",
      data: { message: commentDraft.trim(), versionId: version._id, anchorText: pendingSelection.text, anchorIndex: pendingSelection.anchorIndex },
      onSuccess: () => {
        setSavingComment(false);
        setPendingSelection(null);
        setCommentDraft("");
        fetchVersionComments();
        ShowToast("Comment added", Severty.SUCCESS);
      },
      onError: () => {
        setSavingComment(false);
        ShowToast("Failed to add comment", Severty.ERROR);
      },
    });
  };

  const deleteComment = (commentId) => {
    request({
      url: apiPath.blogWriteRequestDeleteComment(requestDetails._id, commentId),
      method: "DELETE",
      onSuccess: (data) => {
        if (data.status === 200) {
          ShowToast("Comment removed", Severty.SUCCESS);
          setActivePopup((prev) => {
            if (!prev) return null;
            const remaining = prev.comments.filter((c) => String(c._id) !== String(commentId));
            return remaining.length ? { ...prev, comments: remaining } : null;
          });
          fetchVersionComments();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to remove comment", Severty.ERROR);
      },
    });
  };

  const handleContentClick = (e) => {
    const hl = e.target.closest && e.target.closest(".bwv-comment-hl");
    if (!hl) return;
    const id = hl.getAttribute("data-comment-id");
    const matches = versionComments.filter((c) => String(c._id) === String(id));
    if (!matches.length) return;
    const rect = hl.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    setActivePopup({ comments: matches, top: rect.bottom - wrapperRect.top + 6, left: rect.left - wrapperRect.left });
  };

  useEffect(() => {
    if (!open || !version) return;
    setLoadingHtml(true);
    setHtml("");
    request({
      url: apiPath.blogWriteRequestVersionHtml(requestDetails._id, version._id),
      method: "GET",
      onSuccess: (data) => {
        setLoadingHtml(false);
        setHtml(data?.data?.html || "");
        setConversionWarnings(data?.data?.conversionWarnings || 0);
      },
      onError: () => {
        setLoadingHtml(false);
        ShowToast("Failed to load this version's content", Severty.ERROR);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, version?._id]);

  const promote = () => {
    if (!scheduleDate) {
      ShowToast("Pick a date to publish this on", Severty.ERROR);
      return;
    }
    setPromoting(true);
    request({
      url: apiPath.blogWriteRequestPromoteToBlog(requestDetails._id),
      method: "PUT",
      data: {
        selectedDate: scheduleDate.format("YYYY-MM-DD"),
        timezone: moment.tz ? moment.tz.guess() : undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      onSuccess: (data) => {
        setPromoting(false);
        if (data.status === 200) {
          ShowToast("Blog created and scheduled", Severty.SUCCESS);
          setPromotedBlog(data?.data?.blog || null);
          onPromoted?.(data?.data?.blog);
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setPromoting(false);
        ShowToast(err?.response?.data?.message || "Failed to create the blog", Severty.ERROR);
      },
    });
  };

  const isWaitingForReview = requestDetails?.status === "Waiting for Review";
  const isApproved = requestDetails?.status === "Completed";
  const hasBlog = !!requestDetails?.blogId;

  return (
    <Drawer
      title={version?.name || version?.title || (version ? `Version ${version.version}` : "")}
      open={open}
      onClose={onClose}
      width="90%"
      destroyOnClose
    >
      <Spin spinning={loadingHtml}>
        <div ref={wrapperRef} style={{ maxWidth: 820, margin: "0 auto", position: "relative" }}>
          {conversionWarnings > 0 && (
            <Tag color="warning" style={{ marginBottom: 16 }}>
              Converted from the uploaded file — double-check formatting before approving.
            </Tag>
          )}
          <div
            style={{
              display: "flex", alignItems: "center", gap: 6, background: "#eff6ff",
              border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px",
              marginBottom: 10, fontSize: 12, color: "#1d4ed8",
            }}
          >
            <HighlightOutlined /> Select any text below to highlight it and leave a comment for the writer.
          </div>
          {versionComments.length > 0 && (
            <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
              <CommentOutlined /> {versionComments.length} highlighted comment{versionComments.length > 1 ? "s" : ""} — click a highlight to view.
            </p>
          )}
          <div
            ref={contentRef}
            className="gfx-brief-text gfx-brief-text-rich"
            onMouseUp={handleContentMouseUp}
            onClick={handleContentClick}
            dangerouslySetInnerHTML={{
              __html: buildCommentHighlights(linkifyHtml(html), versionComments) || "<p>No content.</p>",
            }}
          />

          <Divider />

          {isWaitingForReview && (
            <div>
              <Button className="gfx-drawer-action-btn" type="primary" icon={<CheckOutlined />} loading={deciding} onClick={onApprove}>
                Approve
              </Button>
              <Button className="gfx-drawer-action-btn" danger icon={<RequestChangesIcon />} disabled={deciding} onClick={onRequestChanges}>
                Request Changes
              </Button>
            </div>
          )}

          {/* Only reachable here when the delivery date had already passed
              by the time this was approved — an on-time approval is
              auto-promoted straight to Draft server-side
              (BlogWriteService.changeStatus), no date needed, since nothing
              publishes automatically from this app anyway. */}
          {isApproved && !hasBlog && (
            <div>
              <h4>This request's delivery date has passed — schedule it to publish</h4>
              <Space wrap>
                <DatePicker placeholder="Publish date" value={scheduleDate} onChange={setScheduleDate} />
                <Button className="gfx-drawer-action-btn" type="primary" icon={<CalendarOutlined />} loading={promoting} onClick={promote}>
                  Schedule &amp; Publish
                </Button>
              </Space>
            </div>
          )}

          {hasBlog && (
            <div>
              <h4>Blog created</h4>
              <Tag color="success">
                {promotedBlog ? "Blog created and scheduled." : "Blog already created for this request."} Manage review/publishing from the Blogs list.
              </Tag>
            </div>
          )}

          {/* Floating "add a comment on this selection" popover — position:absolute
              against wrapperRef (not fixed to the viewport), using coords relative
              to that same ancestor, so it scrolls together with the content instead
              of staying pinned in place while the highlight moves away underneath it. */}
          {pendingSelection && (
            <div
              style={{
                position: "absolute", top: pendingSelection.top, left: pendingSelection.left,
                zIndex: 1100, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)", padding: 10, width: 280,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Comment on selected text</span>
                <CloseOutlined
                  style={{ cursor: "pointer", fontSize: 10, color: "#999", background: "transparent", border: "none", padding: 0, boxShadow: "none" }}
                  onClick={() => { setPendingSelection(null); setCommentDraft(""); }}
                />
              </div>
              <p style={{ fontSize: 11, color: "#888", fontStyle: "italic", margin: "0 0 6px", maxHeight: 40, overflow: "hidden" }}>
                "{pendingSelection.text.length > 80 ? pendingSelection.text.slice(0, 80) + "…" : pendingSelection.text}"
              </p>
              <TextArea
                rows={2}
                autoFocus
                placeholder="What should the writer change here?"
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
              />
              <Button
                className="gfx-drawer-action-btn"
                type="primary"
                size="small"
                style={{ marginTop: 8, height: 26 }}
                loading={savingComment}
                disabled={!commentDraft.trim()}
                onClick={submitInlineComment}
              >
                Add Comment
              </Button>
            </div>
          )}

          {/* Floating "view this comment" popover — shown when an existing
              highlight is clicked (handleContentClick). Also position:absolute
              against wrapperRef, for the same scroll-with-content reason above. */}
          {activePopup && (
            <div
              style={{
                position: "absolute", top: activePopup.top, left: activePopup.left,
                zIndex: 1100, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)", padding: "10px 26px 8px 12px", width: 280,
                maxHeight: 240, overflowY: "auto",
              }}
            >
              <CloseOutlined
                style={{
                  position: "absolute", top: 10, right: 10, cursor: "pointer", fontSize: 10, color: "#999",
                  background: "transparent", border: "none", padding: 0, lineHeight: 1, boxShadow: "none",
                }}
                onClick={() => setActivePopup(null)}
              />
              {activePopup.comments.map((c, i) => (
                <div
                  key={c._id}
                  style={{
                    marginBottom: 10, paddingTop: i === 0 ? 0 : 8,
                    borderTop: i === 0 ? "none" : "1px solid #f0f0f0",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>
                    {c.authorName || "Team member"}
                  </div>
                  <div style={{ fontSize: 13, color: "#444", margin: "3px 0 6px" }}>{c.message}</div>
                  <div style={{ textAlign: "right" }}>
                    <span
                      style={{ fontSize: 11, color: "#e11d48", cursor: "pointer" }}
                      onClick={() => deleteComment(c._id)}
                      title="Remove this highlight and comment"
                    >
                      <DeleteOutlined /> Remove
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Spin>
    </Drawer>
  );
}

export default BlogWriteVersionEditor;
